import type { StateCreator } from "zustand";

interface FormData {
  [formId: string]: {
    values: Record<string, unknown> | Record<string, unknown>[];
    completed: boolean;
  };
}

interface CvPageFormsSliceState {
  formData: FormData;
  readyToSend: boolean;
  showCompletionScreen: boolean;
  isFinishing: boolean;
  isUserReadyToSend: boolean;
}

interface CvPageFormsSliceActions {
  setFormData: (formId: string, data: Record<string, unknown>, cb?: string) => void;
  setFormDatas: (formId: string, data: Record<string, unknown>[]) => void;
  resetFormData: (formId: string) => void;
  setCompleted: (formId: string, completed: boolean) => void;
  deleteFormDataValues: (formId: string) => void;
  setReadyToSend: (readyToSend: boolean) => void;
  setShowCompletionScreen: (showCompletionScreen: boolean) => void;
  setIsFinishing: (isFinishing: boolean) => void;
  setIsUserReadyToSend: (isUserReadyToSend: boolean) => void;
  resetCvPageState: () => void;
}

export type CvPageFormsSlice = CvPageFormsSliceState & CvPageFormsSliceActions;

export const createCvPageFormsSlice: StateCreator<
  CvPageFormsSlice,
  [["zustand/devtools", never]],
  [],
  CvPageFormsSlice
> = (set) => ({
  formData: {},
  readyToSend: false,
  showCompletionScreen: false,
  isFinishing: false,
  isUserReadyToSend: false,
  setFormData: (formId, data, _cb) =>
    set((state) => {
      const currentForm = state.formData[formId] || { completed: false, values: {} };
      const updatedValues = { ...currentForm.values, ...data };

      // Only update if values actually changed to prevent unnecessary re-renders
      if (JSON.stringify(currentForm.values) === JSON.stringify(updatedValues)) {
        return state; // No change, return same state
      }

      return {
        formData: {
          ...state.formData,
          [formId]: {
            ...currentForm,
            values: updatedValues,
          },
        },
      };
    }),
  setFormDatas: (formId, data) =>
    set((state) => ({
      formData: {
        ...state.formData,
        [formId]: { ...state.formData[formId], values: data },
      },
    })),
  resetFormData: (formId) =>
    set((state) => {
      const newFormData = { ...state.formData };
      delete newFormData[formId];
      return { formData: newFormData };
    }),
  deleteFormDataValues: (formId) =>
    set((state) => {
      return {
        ...state,
        formData: {
          ...state.formData,
          [formId]: { values: {}, completed: false },
        },
      };
    }),
  setCompleted: (formId, completed) =>
    set((state) => ({
      formData: {
        ...state.formData,
        [formId]: {
          ...(state.formData[formId] || { values: {} }),
          completed,
        },
      },
    })),
  setReadyToSend: (readyToSend) => set({ readyToSend }),
  setShowCompletionScreen: (showCompletionScreen) => {
    console.log("[Zustand] setShowCompletionScreen called", { showCompletionScreen });
    set({ showCompletionScreen });
  },
  setIsFinishing: (isFinishing) => {
    console.log("[Zustand] setIsFinishing called", { isFinishing });
    set({ isFinishing });
  },
  setIsUserReadyToSend: (isUserReadyToSend) => set({ isUserReadyToSend }),
  resetCvPageState: () =>
    set({
      showCompletionScreen: false,
      isFinishing: false,
      isUserReadyToSend: false,
    }),
});
